// components/survey-questions/SurveyQuestions.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SurveyService from '../../services/survey.service';
import { Edit, Trash2, Plus  } from 'lucide-react';
import TagService from '../../services/tag.service';

const SurveyQuestions = () => {
  const [questions, setQuestions] = useState([]);
  const [tags, setTags] = useState([]);
  const [selectedTag, setSelectedTag] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([
      loadQuestions(),
      loadTags()
    ]).then(() => {
      setLoading(false);
    }).catch(error => {
      console.error('Errore durante il caricamento dei dati:', error);
      setError('Impossibile caricare i dati');
      setLoading(false);
    });
  }, [])

  const loadQuestions = async () => {
    try {
      const data = await SurveyService.getAllQuestions();
      const sortedQuestions = [...data].sort((a, b) => a.order - b.order);
      setQuestions(sortedQuestions);
      return data;
    } catch (error) {
      console.error('Errore durante il caricamento delle domande:', error);
      setError('Impossibile caricare le domande del questionario');
      return [];
    }
  };

  const loadTags = async () => {
    try {
      const data = await TagService.getTags(); 
      console.log('Tags caricati in SurveyQuestions:', data); 
      setTags(data);
      return data;
    } catch (error) {
      console.error('Errore durante il caricamento dei tag:', error);
      return [];
    }
  };

  const handleTagChange = (e) => {
    setSelectedTag(e.target.value);
  };

  const filteredQuestions = selectedTag === 'all' 
    ? questions 
    : questions.filter(q => q.tag_id === parseInt(selectedTag));

  const handleEdit = (id) => {
    navigate(`/admin/survey/questions/${id}`);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Sei sicuro di voler eliminare questa domanda?')) {
      try {
        await SurveyService.deleteQuestion(id);
        loadQuestions(); // Ricarica le domande dopo l'eliminazione
      } catch (error) {
        console.error('Errore durante l\'eliminazione della domanda:', error);
        setError('Errore durante l\'eliminazione della domanda');
      }
    }
  };


  const handleAddQuestion = () => {
    navigate('/admin/survey/questions/new');
  };

  if (loading) {
    return <div className="p-4">Caricamento domande...</div>;
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold">Domande del Questionario</h2>
        <button 
          onClick={handleAddQuestion}
          className="bg-purple-600 text-white px-4 py-2 rounded-md flex items-center hover:bg-purple-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Aggiungi Domanda
        </button>
      </div>
      
      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md">
          {error}
        </div>
      )}
      
      {/* Filtro per tag */}
      <div className="mb-6">
        <label htmlFor="tag-filter" className="block text-sm font-medium text-gray-700 mb-1">
          Filtra per Tag:
        </label>
        <select
          id="tag-filter"
          value={selectedTag}
          onChange={handleTagChange}
          className="w-full md:w-64 p-2 border border-gray-300 rounded-md"
        >
          <option value="all">Tutte le categorie</option>
          {tags.map(tag => (
            <option key={tag.id} value={tag.id}>{tag.name}</option>
          ))}
        </select>
      </div>
      
      {filteredQuestions.length === 0 ? (
        <p className="text-gray-600">
          {selectedTag === 'all' 
            ? 'Nessuna domanda trovata nel questionario.' 
            : 'Nessuna domanda trovata per questa categoria.'}
        </p>
      ) : (
        <div className="space-y-4">
          {filteredQuestions.map((question, index) => (
            <div key={question.id} className="border rounded-lg p-4 bg-gray-50">
              <div className="flex justify-between">
                <div>
                <div className="flex items-center">
          <span className="inline-flex items-center justify-center bg-purple-100 text-purple-800 text-xs font-medium h-6 w-6 rounded-full mr-2">
          {question.order}
          </span>
          <h3 className="font-medium text-lg">{question.title}</h3>
        </div>
                          <p className="text-gray-600 mt-1">{question.description}</p>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-md text-xs">
                     {tags.find(t => t.id === parseInt(question.tag_id))?.name || 'Tag non specificato'}
                    </span>
                    <span className="px-2 py-1 bg-gray-200 text-gray-700 rounded-md text-xs">
                      {question.type === 'text' ? 'Risposta testuale' : 
                        question.allows_multiple ? 'Scelta multipla' : 'Scelta singola'}
                    </span>
                    {question.has_text_input && (
                      <span className="px-2 py-1 bg-gray-200 text-gray-700 rounded-md text-xs">
                        Con input di testo
                      </span>
                    )}
                  </div>
                  
                  {question.type !== 'text' && question.options?.length > 0 && (
                    <div className="mt-3">
                      <p className="text-sm text-gray-500 mb-1">Opzioni:</p>
                      <ul className="list-disc list-inside text-sm pl-2">
                        {question.options.map(option => (
                          <li key={option.id} className="text-gray-600">{option.description}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                
                <div className="flex flex-col space-y-2">
                  <button 
                    onClick={() => handleEdit(question.id)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                  >
                    <Edit className="w-5 h-5" />
                  </button>
                  <button 
                    onClick={() => handleDelete(question.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SurveyQuestions;